import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { useAuth } from '../../context/AuthContext';
import { useSlashSearchShortcut } from '../../hooks/useSlashSearchShortcut';
import { FillPage } from '../../layout/FillPage';
import { useNavCounts } from '../../layout/useNavCounts';
import { intentHue } from '../../shared/intent';
import { formatPhone } from '../../shared/phone';
import { formatShortDateTime, relativeTime } from '../../shared/time';
import { useDebouncedValue } from '../../shared/useDebouncedValue';
import { useHeld, useLeave } from '../../shared/useLeave';
import type {
  SmsConversationListItem,
  SmsMessage,
} from '../../types/messaging';
import {
  Avatar,
  Button,
  Callout,
  Checkbox,
  Composer,
  Count,
  EmptyState,
  IconArchive,
  IconCheck,
  IconChevronLeft,
  IconEye,
  IconEyeOff,
  IconInbox,
  IconMessage,
  IconSearch,
  IconSparkle,
  InboxListSkeleton,
  InboxRow,
  Input,
  Kbd,
  MessageThread,
  RefreshFailed,
  Segmented,
  Skeleton,
  StatusLabel,
  Tag,
  Workspace,
} from '../../ui/v2';
import {
  type ConversationPatch,
  fetchEarlier,
  LIST_PAGE_SIZE,
  useConversation,
  useConversationList,
  useSmsActions,
  useViewCounts,
} from './smsData';
import {
  answering,
  displayName,
  firstName,
  handoffSentence,
  leavesView,
  type Me,
  QUIET_ON_ROW,
  toThread,
  type View,
  VIEW_LABEL,
  viewFromParams,
  VIEWS,
} from './smsModel';

/**
 * Texts: master/detail. The list answers "who's waiting"; the detail
 * answers "who's answering" (the handoff row) and shows the thread as a
 * script (SMS_V2_SPEC.md).
 */

export const SMS_BASE = '/sms';

export default function SmsInbox() {
  const [params, setParams] = useSearchParams();
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const me: Me | null = user ? { id: user.id, email: user.email } : null;

  const view = viewFromParams(params);
  const unreadOnly = ['true', '1'].includes(params.get('unread') ?? '');
  const [searchInput, setSearchInput] = useState(params.get('q') ?? '');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [limit, setLimit] = useState(LIST_PAGE_SIZE);
  const searchRef = useRef<HTMLInputElement>(null);
  useSlashSearchShortcut(searchRef);

  const setParam = (patch: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        // Legacy `?status=closed` is a view. Keep it when search or unread
        // rewrite the query; an explicit view replaces it.
        if (
          !('view' in patch) &&
          !next.get('view') &&
          viewFromParams(next) === 'closed'
        ) {
          next.set('view', 'closed');
        }
        next.delete('status');
        Object.entries(patch).forEach(([k, v]) =>
          v ? next.set(k, v) : next.delete(k),
        );
        return next;
      },
      { replace: true },
    );

  useEffect(() => {
    if ((params.get('q') ?? '') !== search) setParam({ q: search || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => setLimit(LIST_PAGE_SIZE), [view, unreadOnly, search]);

  const list = useConversationList({ view, unread: unreadOnly, search, limit });
  const counts = useViewCounts(search);
  const unreadTotal = useNavCounts({ sms: true }).sms;
  const actions = useSmsActions();
  const [markingRead, setMarkingRead] = useState(false);

  const rows = useMemo(() => list.data?.rows ?? [], [list.data]);
  const selectedId = id && /^\d+$/.test(id) ? Number(id) : null;
  const selectedRow = rows.find((c) => c.id === selectedId) ?? null;
  const idx = rows.findIndex((c) => c.id === selectedId);

  const open = (c: SmsConversationListItem | null) => {
    const qs = params.toString();
    navigate(`${c ? `${SMS_BASE}/${c.id}` : SMS_BASE}${qs ? `?${qs}` : ''}`);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if ((e.key === 'ArrowDown' || e.key === 'j') && idx < rows.length - 1) {
        e.preventDefault();
        open(rows[idx + 1]);
      }
      if ((e.key === 'ArrowUp' || e.key === 'k') && idx > 0) {
        e.preventDefault();
        open(rows[idx - 1]);
      }
      if (e.key === 'Escape' && selectedId != null) open(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, rows, selectedId, params]);

  const searchField = (ref?: React.Ref<HTMLInputElement>) => (
    <Input
      ref={ref}
      size="sm"
      type="search"
      placeholder="Search number or text…"
      value={searchInput}
      onChange={(e) => setSearchInput(e.target.value)}
      leading={<IconSearch size={14} />}
      trailing={!searchInput && ref ? <Kbd>/</Kbd> : undefined}
      aria-label="Search conversations"
    />
  );

  const hasMore = !!list.data && list.data.total > rows.length;

  return (
    <FillPage>
      <PageMeta title="AskArthur | Texts" description="SMS conversations" />
      <Workspace
        title="Texts"
        meta={
          unreadTotal > 0
            ? `${unreadTotal} unread`
            : counts.data
              ? `${counts.data.inbox} open`
              : undefined
        }
        toolbar={
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5 px-4 py-2.5 lg:px-6 lg:py-3">
            <div className="flex max-w-full items-center gap-4 overflow-x-auto">
              <Segmented<View>
                aria-label="View"
                className="shrink-0"
                items={VIEWS.map((v) => ({
                  key: v,
                  label: VIEW_LABEL[v],
                  count: counts.data?.[v],
                }))}
                value={view}
                onChange={(v) => setParam({ view: v === 'inbox' ? null : v })}
              />
              <Checkbox
                label="Unread only"
                checked={unreadOnly}
                onChange={(e) =>
                  setParam({ unread: e.target.checked ? 'true' : null })
                }
                className="hidden shrink-0 whitespace-nowrap sm:inline-flex"
              />
            </div>
            <div className="ml-auto flex max-w-full shrink-0 items-center gap-4">
              {unreadTotal > 0 && (
                <Button
                  size="sm"
                  variant="secondary"
                  leading={<IconCheck size={14} />}
                  loading={markingRead}
                  onClick={() => {
                    setMarkingRead(true);
                    void actions
                      .markAllRead()
                      .catch(() => undefined)
                      .finally(() => setMarkingRead(false));
                  }}
                  className="shrink-0"
                >
                  Mark all read
                </Button>
              )}
              <div className="hidden w-[240px] min-w-[140px] shrink md:block">
                {searchField(searchRef)}
              </div>
            </div>
          </div>
        }
      >
        <div className="flex min-h-0 flex-1">
          <div
            className={clsx(
              'flex min-h-0 w-full flex-col bg-v2-surface lg:w-[380px] lg:shrink-0 lg:border-r lg:border-v2-line',
              selectedId != null && 'hidden lg:flex',
            )}
          >
            <div className="border-b border-v2-line px-4 py-2 md:hidden">
              {searchField()}
            </div>
            {list.isRefetchError && list.data && (
              <RefreshFailed
                className="m-3"
                what="Texts"
                onRetry={() => void list.refetch()}
              />
            )}
            {list.isError && !list.data ? (
              <div className="p-4">
                <Callout tone="danger" title="Couldn’t load conversations">
                  {list.error.message}
                  <div className="mt-2">
                    <Button size="sm" onClick={() => void list.refetch()}>
                      Retry
                    </Button>
                  </div>
                </Callout>
              </div>
            ) : list.isPending ? (
              <InboxListSkeleton label="Loading conversations" rows={7} />
            ) : rows.length === 0 ? (
              <EmptyList view={view} unreadOnly={unreadOnly} search={search} />
            ) : (
              <div className="v2-scroll min-h-0 flex-1 overflow-y-auto">
                <ul role="listbox" aria-label="Conversations">
                  {rows.map((c) => (
                    <ConversationRow
                      key={c.id}
                      c={c}
                      me={me}
                      selected={c.id === selectedId}
                      onOpen={() => open(c)}
                    />
                  ))}
                </ul>
                {hasMore && (
                  <div className="flex justify-center p-3">
                    <Button
                      size="sm"
                      loading={list.isFetching}
                      onClick={() => setLimit((n) => n + LIST_PAGE_SIZE)}
                    >
                      Load more
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            className={clsx(
              'flex min-h-0 min-w-0 flex-1 flex-col bg-v2-surface',
              selectedId == null && 'hidden lg:flex',
            )}
          >
            {selectedId != null ? (
              <Conversation
                key={selectedId}
                id={selectedId}
                row={selectedRow}
                view={view}
                me={me}
                onBack={() => open(null)}
                onMarkRead={actions.markRead}
              />
            ) : (
              <div className="v2-fade-in">
                <EmptyState
                  icon={<IconMessage />}
                  title="Select a conversation"
                  description="Pick a thread on the left, or press ↓ to open the first one."
                />
              </div>
            )}
          </div>
        </div>
      </Workspace>
    </FillPage>
  );
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

const EmptyList: React.FC<{
  view: View;
  unreadOnly: boolean;
  search: string;
}> = ({ view, unreadOnly, search }) => (
  <EmptyState
    icon={view === 'archived' ? <IconArchive /> : <IconMessage />}
    title={
      search
        ? `No conversations match “${search}”`
        : unreadOnly
          ? 'Nothing unread'
          : view === 'needs'
            ? 'Nobody is waiting for you'
            : view === 'closed'
              ? 'No closed conversations'
              : view === 'archived'
                ? 'No archived conversations'
                : 'No texts yet'
    }
    description={
      search
        ? 'Try the last four digits of the number, or a word from the message.'
        : view === 'needs'
          ? 'When Arthur hands a conversation off, it shows up here.'
          : view === 'archived'
            ? 'Conversations you archive move here. A new text brings one back.'
            : 'When guests text the store, the conversation appears here.'
    }
  />
);

const ConversationRow: React.FC<{
  c: SmsConversationListItem;
  me: Me | null;
  selected: boolean;
  onOpen: () => void;
}> = ({ c, me, selected, onOpen }) => {
  const a = answering(c, me);
  const unread = c.unread_count > 0;
  return (
    <InboxRow
      selected={selected}
      unread={unread}
      onOpen={onOpen}
      avatar={
        <Avatar
          name={c.customer_name}
          identity={c.customer ?? c.customer_phone_e164}
          hue="auto"
          size="md"
        />
      }
      title={displayName(c)}
      anonymous={!c.customer_name?.trim()}
      at={c.last_message_at}
      atLabel={relativeTime(c.last_message_at)}
      preview={c.last_message_preview}
      trailing={
        <>
          {!QUIET_ON_ROW.includes(a.kind) && (
            <StatusLabel
              size="sm"
              tone={a.tone}
              hollow={a.hollow}
              className="shrink-0 !text-v2-11"
            >
              {a.label}
            </StatusLabel>
          )}
          {unread && (
            <Count
              value={c.unread_count}
              className="!ml-0 !rounded-full !bg-v2-accent px-1.5 !text-v2-on-accent"
            />
          )}
        </>
      }
    />
  );
};

// ---------------------------------------------------------------------------
// Conversation
// ---------------------------------------------------------------------------

/**
 * One thread. Closing or archiving it moves it out of most views, so the
 * panel slides out and the list is left selected on nothing; `view` says
 * whether the conversation is leaving this one.
 */
const Conversation: React.FC<{
  id: number;
  row: SmsConversationListItem | null;
  view: View;
  me: Me | null;
  onBack: () => void;
  onMarkRead: (id: number) => Promise<void>;
}> = ({ id, row, view, me, onBack, onMarkRead }) => {
  const detail = useConversation(id);
  const actions = useSmsActions();
  const { leaving, leave } = useLeave(onBack);
  // While the panel leaves, the row drops out of the list and the record
  // flips to closed or archived; keep showing what was acted on.
  const c = useHeld(detail.data ?? row, leaving);

  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [earlier, setEarlier] = useState<SmsMessage[]>([]);
  const [moreEarlier, setMoreEarlier] = useState<boolean | null>(null);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [earlierError, setEarlierError] = useState<string | null>(null);

  // Opening a thread reads it, and so does a new message arriving while open,
  // unless staff marked it unread here; that holds until the thread changes.
  const heldUnread = useRef<number | null>(null);
  const unread = detail.data?.unread_count ?? 0;
  useEffect(() => {
    if (unread > 0 && heldUnread.current !== id) void onMarkRead(id);
  }, [id, unread, onMarkRead]);

  const toggleRead = async () => {
    setBusy('read');
    setActionError(null);
    try {
      if (unread > 0) {
        heldUnread.current = null;
        await onMarkRead(id);
      } else {
        heldUnread.current = id;
        await actions.markUnread(id);
      }
    } catch (e) {
      heldUnread.current = null;
      setActionError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const messages = useMemo(() => {
    const recent = detail.data?.messages ?? [];
    const seen = new Set(recent.map((m) => m.id));
    return [...earlier.filter((m) => !seen.has(m.id)), ...recent];
  }, [earlier, detail.data?.messages]);
  const thread = useMemo(
    () => toThread(messages, c?.customer_name),
    [messages, c?.customer_name],
  );
  const hasEarlier = moreEarlier ?? !!detail.data?.messages_has_more;

  if (detail.isError && !detail.data) {
    return (
      <div className="p-6">
        <Callout tone="danger" title="Couldn’t load this conversation">
          {detail.error.message}
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={() => void detail.refetch()}>
              Retry
            </Button>
            <Button size="sm" variant="ghost" onClick={onBack}>
              Back to Texts
            </Button>
          </div>
        </Callout>
      </div>
    );
  }

  if (!c) return <ThreadSkeleton />;

  const a = answering(c, me);
  const guestFirst = firstName(c.customer_name);

  const run = async (
    name: string,
    payload: ConversationPatch,
    leaves = false,
  ) => {
    setBusy(name);
    setActionError(null);
    const action = () => actions.update(id, payload);
    try {
      await (leaves ? leave(action) : action());
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    const body = draft.trim();
    if (!body || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setSendError(null);
    try {
      await actions.send(id, body, me);
      setDraft('');
    } catch (e) {
      setSendError((e as Error).message);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const loadEarlier = async () => {
    const first = messages[0];
    if (!first) return;
    setLoadingEarlier(true);
    setEarlierError(null);
    try {
      const page = await fetchEarlier(id, first.id);
      setEarlier((prev) => [...page.results, ...prev]);
      setMoreEarlier(page.has_more_before);
    } catch (e) {
      setEarlierError((e as Error).message);
    } finally {
      setLoadingEarlier(false);
    }
  };

  const reopen = () => run('reopen', { is_closed: false, status: 'OPEN' });

  return (
    <section
      aria-label={`Conversation with ${displayName(c)}`}
      className={clsx(
        'flex min-h-0 flex-1 flex-col',
        leaving && 'v2-slide-out-right',
      )}
    >
      <header className="flex shrink-0 items-center gap-3 border-b border-v2-line px-4 py-3.5 lg:px-6">
        <Button
          size="sm"
          variant="ghost"
          leading={<IconChevronLeft size={14} />}
          onClick={onBack}
          className="-ml-2 lg:hidden"
        >
          Texts
        </Button>
        <Avatar
          name={c.customer_name}
          identity={c.customer ?? c.customer_phone_e164}
          hue="auto"
          size="lg"
          className="hidden lg:inline-flex"
        />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <h2
              className={clsx(
                'truncate text-v2-18 font-bold tracking-[-0.015em]',
                !c.customer_name?.trim() && 'v2-num',
              )}
            >
              {displayName(c)}
            </h2>
            {c.primary_category && (
              <Tag tone={intentHue(c.primary_category)} className="shrink-0">
                {humanize(c.primary_category)}
              </Tag>
            )}
            {c.is_archived && (
              <Tag tone="neutral" className="shrink-0">
                Archived
              </Tag>
            )}
          </div>
          {(c.last_message_at || c.customer_name?.trim()) && (
            <p className="v2-num mt-0.5 flex min-w-0 items-center gap-1.5 text-v2-12 text-v2-fg-3">
              <IconMessage size={13} className="shrink-0 text-v2-accent" />
              <span className="truncate">
                {c.last_message_at && (
                  <time dateTime={c.last_message_at}>
                    {formatShortDateTime(c.last_message_at)}
                  </time>
                )}
                {c.last_message_at && c.customer_name?.trim() ? ' · ' : ''}
                {c.customer_name?.trim()
                  ? formatPhone(c.customer_phone_e164)
                  : ''}
              </span>
            </p>
          )}
        </div>
      </header>

      <div className="flex min-h-10 shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-v2-line bg-v2-sunken/40 px-4 py-2 lg:px-6">
        <StatusLabel tone={a.tone} hollow={a.hollow}>
          {a.label}
        </StatusLabel>
        <span className="hidden min-w-0 flex-1 truncate text-v2-12 text-v2-fg-3 sm:inline">
          {handoffSentence(a)}
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            leading={
              unread > 0 ? <IconEye size={14} /> : <IconEyeOff size={14} />
            }
            loading={busy === 'read'}
            onClick={() => void toggleRead()}
          >
            <span className="max-sm:sr-only">
              {unread > 0 ? 'Mark read' : 'Mark unread'}
            </span>
          </Button>
          {!c.is_closed &&
            me?.id != null &&
            a.kind !== 'staff' &&
            a.kind !== 'off' && (
              <Button
                size="sm"
                loading={busy === 'take'}
                onClick={() =>
                  run('take', { assigned_to: me.id, needs_human: false })
                }
              >
                Take over
              </Button>
            )}
          {!c.is_closed && a.kind === 'staff' && (
            <Button
              size="sm"
              loading={busy === 'back'}
              leading={
                c.agent_status === 'DISABLED' ? undefined : (
                  <IconSparkle size={12} />
                )
              }
              onClick={() => run('back', { assigned_to: null })}
            >
              {c.agent_status === 'DISABLED'
                ? 'Unassign'
                : 'Hand back to Arthur'}
            </Button>
          )}
          {c.is_closed ? (
            <Button size="sm" loading={busy === 'reopen'} onClick={reopen}>
              Reopen
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              loading={busy === 'close'}
              onClick={() =>
                run('close', { status: 'CLOSED' }, leavesView(view, 'close'))
              }
            >
              Close
            </Button>
          )}
          {c.is_archived ? (
            <Button
              size="sm"
              variant="ghost"
              leading={<IconInbox size={14} />}
              loading={busy === 'archive'}
              onClick={() => run('archive', { archived: false })}
            >
              <span className="max-sm:sr-only">Restore</span>
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              leading={<IconArchive size={14} />}
              loading={busy === 'archive'}
              onClick={() =>
                run('archive', { archived: true }, leavesView(view, 'archive'))
              }
            >
              <span className="max-sm:sr-only">Archive</span>
            </Button>
          )}
        </span>
      </div>
      {actionError && (
        <Callout
          tone="danger"
          className="mx-4 mt-3 lg:mx-6"
          title="That didn’t go through"
        >
          {actionError}
        </Callout>
      )}

      {detail.isPending ? (
        <ThreadSkeleton bare />
      ) : (
        <MessageThread
          messages={thread}
          guestName={c.customer_name?.trim() || null}
          guestIdentity={c.customer ?? c.customer_phone_e164}
          className="min-h-0 flex-1"
          lead={
            hasEarlier || earlierError ? (
              <div className="mb-4 flex flex-col items-center gap-2">
                {earlierError && (
                  <p className="text-v2-12 text-v2-danger">{earlierError}</p>
                )}
                {hasEarlier && (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={loadingEarlier}
                    onClick={() => void loadEarlier()}
                  >
                    Load earlier messages
                  </Button>
                )}
              </div>
            ) : undefined
          }
          empty={
            <EmptyState
              compact
              icon={<IconMessage />}
              title="No messages yet"
            />
          }
        />
      )}

      <Composer
        value={draft}
        onChange={setDraft}
        onSend={() => void send()}
        sending={sending}
        error={sendError}
        onDismissError={() => setSendError(null)}
        disabledReason={
          c.is_closed ? (
            <span className="inline-flex flex-wrap items-center gap-2">
              This conversation is closed.
              <Button size="sm" loading={busy === 'reopen'} onClick={reopen}>
                Reopen to reply
              </Button>
            </span>
          ) : undefined
        }
        placeholder={`Text ${guestFirst ?? 'the guest'}…`}
        aside={me?.email ? `Replying as ${me.email}` : undefined}
      />
    </section>
  );
};

const ThreadSkeleton: React.FC<{ bare?: boolean }> = ({ bare }) => (
  <div aria-label="Loading conversation" className="flex-1 px-4 py-4 lg:px-6">
    {!bare && <Skeleton className="mb-6 h-4" width="30%" />}
    {Array.from({ length: 4 }).map((_, i) => (
      <div
        key={i}
        className={clsx('mb-5 flex gap-2.5', i % 2 === 1 && 'flex-row-reverse')}
      >
        <Skeleton className="h-6 w-6 rounded-full" />
        <Skeleton
          className="h-9 rounded-v2-lg"
          width={`${48 - (i % 3) * 10}%`}
        />
      </div>
    ))}
  </div>
);

/** `RESERVATION` → "Reservation"; free-text labels pass through. */
function humanize(label: string): string {
  if (label !== label.toUpperCase()) return label;
  const words = label.toLowerCase().replace(/_/g, ' ');
  return words[0].toUpperCase() + words.slice(1);
}
